import { GrowthBookService, GrowthBookFeature } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';

/** GrowthBook multivariate: billing v2 explicit plan selection (A/B/C). */
export const EXPLICIT_PLAN_SELECTION_FEATURE = 'explicit-plan-selection' as const;

/**
 * When `/new-billing?choosePlan=yes` was opened from payment-method Add Card
 * (explicit-plan-selection B/C, no card on file). Other CTAs omit this param.
 */
export const CHOOSE_PLAN_INTENT_QUERY_PARAM = 'choosePlanIntent' as const;
export const CHOOSE_PLAN_INTENT_ADD_CARD = 'addCard' as const;

/**
 * Dev/QA: `?td_internal=cardEntryB` forces billing v2 explicit-plan-selection **B** UI.
 * Only **exact** known tokens match (no fuzzy regex) so typos like `cardEntrYB` do not activate B.
 */
const TD_INTERNAL_EXPLICIT_PLAN_MAP: Readonly<Record<string, ExperimentProps['variationid']>> = {
  cardEntryA: 'A',
  cardEntryB: 'B',
  cardEntryC: 'C',
  cardentrya: 'A',
  cardentryb: 'B',
  cardentryc: 'C',
  'card-entry-a': 'A',
  'card-entry-b': 'B',
  'card-entry-c': 'C',
  explicitPlanA: 'A',
  explicitPlanB: 'B',
  explicitPlanC: 'C',
  explicitplana: 'A',
  explicitplanb: 'B',
  explicitplanc: 'C',
  'explicit-plan-a': 'A',
  'explicit-plan-b': 'B',
  'explicit-plan-c': 'C',
};

export function parseExplicitPlanTdInternalParam(raw: string | null | undefined): ExperimentProps['variationid'] | null {
  if (raw == null || raw === '') {
    return null;
  }
  const key = raw.trim();
  return TD_INTERNAL_EXPLICIT_PLAN_MAP[key] ?? null;
}

export function explicitPlanSelectionVariationFromGrowthBook(
  growthBook: GrowthBookService,
): ExperimentProps['variationid'] | null {
  const exp = growthBook.getFeatureValue<ExperimentProps>(
    EXPLICIT_PLAN_SELECTION_FEATURE as GrowthBookFeature,
  );
  if (exp?.variationid === 'A' || exp?.variationid === 'B' || exp?.variationid === 'C') {
    return exp.variationid;
  }
  return null;
}

/** `td_internal` wins over GrowthBook when it maps to A/B/C. */
export function resolveExplicitPlanSelectionVariation(
  growthBook: GrowthBookService,
  tdInternal: string | null | undefined,
): ExperimentProps['variationid'] | null {
  const fromUrl = parseExplicitPlanTdInternalParam(tdInternal);
  if (fromUrl) {
    return fromUrl;
  }
  return explicitPlanSelectionVariationFromGrowthBook(growthBook);
}

export function isExplicitPlanSelectionB(
  growthBook: GrowthBookService,
  tdInternal: string | null | undefined,
): boolean {
  return resolveExplicitPlanSelectionVariation(growthBook, tdInternal) === 'B';
}

export function isExplicitPlanSelectionC(
  growthBook: GrowthBookService,
  tdInternal: string | null | undefined,
): boolean {
  return resolveExplicitPlanSelectionVariation(growthBook, tdInternal) === 'C';
}

/** Variant B or C: shared billing UI (hide top trial payment banner, hide checkout trial strip). */
export function isExplicitPlanSelectionBOrC(
  growthBook: GrowthBookService,
  tdInternal: string | null | undefined,
): boolean {
  const v = resolveExplicitPlanSelectionVariation(growthBook, tdInternal);
  return v === 'B' || v === 'C';
}
